import { useState } from 'react';
import { Search, Globe2, Target, Coffee, Plus, X, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import Card from '@/components/ui/Card';
import type { WelcomeMission } from './useWelcomeState';

interface Props {
  mission: WelcomeMission | '';
  researchBrands: string[];
  onChange: (partial: { mission?: WelcomeMission; researchBrands?: string[] }) => void;
}

interface MissionOption {
  value: WelcomeMission;
  label: string;
  icon: typeof Search;
  tagline: string;
  description: string;
  estimatedMinutes?: number;
}

const MISSIONS: MissionOption[] = [
  {
    value: 'discovery',
    label: 'Find me dropship suppliers',
    icon: Search,
    tagline: 'Default first move — broad supplier hunt.',
    description: "Dippy spends ~15 min searching brand programs, Alibaba/1688, and wholesale distributors. Returns 20–25 ranked prospects and starts researching the best ones.",
    estimatedMinutes: 15,
  },
  {
    value: 'marketplace_sourcing',
    label: 'Source from Alibaba marketplaces',
    icon: Globe2,
    tagline: 'Deep marketplace focus.',
    description: "Dippy goes deep on Alibaba and 1688 specifically — best for commodity categories where brand doesn't matter and price wins.",
    estimatedMinutes: 20,
  },
  {
    value: 'research',
    label: 'Research a brand I already know',
    icon: Target,
    tagline: 'Skip discovery, focus on specific brands.',
    description: "Dippy digs into each brand you name — wholesale program, contact info, dropship terms, integration options. Best when you already have partners in mind.",
    estimatedMinutes: 8,
  },
  {
    value: 'skip',
    label: "Skip — I'll start Dippy myself later",
    icon: Coffee,
    tagline: 'No task fired.',
    description: "Saves your settings but doesn't launch anything. You can fire missions later from the AI Agent dashboard.",
  },
];

export default function WelcomeStep4Mission({ mission, researchBrands, onChange }: Props) {
  const [newBrand, setNewBrand] = useState('');

  function addBrand() {
    const brand = newBrand.trim();
    if (!brand || researchBrands.includes(brand)) return;
    onChange({ researchBrands: [...researchBrands, brand] });
    setNewBrand('');
  }

  function removeBrand(brand: string) {
    onChange({ researchBrands: researchBrands.filter((b) => b !== brand) });
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-text-secondary">
        What should Dippy do first? You can change direction or fire new missions anytime from the AI Agent dashboard.
      </p>

      <div className="space-y-3">
        {MISSIONS.map((m) => {
          const isSelected = mission === m.value;
          const Icon = m.icon;
          return (
            <button
              type="button"
              key={m.value}
              onClick={() => onChange({ mission: m.value })}
              data-testid={`mission-${m.value}`}
              className={cn(
                'p-5 text-left w-full rounded-xl bg-white shadow-sm border-2 transition-all',
                isSelected
                  ? 'border-sage-500 bg-sage-50'
                  : 'border-stone-200 hover:border-sage-300'
              )}
            >
              <div className="flex items-start gap-4">
                <div
                  className={cn(
                    'shrink-0 w-11 h-11 rounded-lg flex items-center justify-center',
                    isSelected ? 'bg-sage-600 text-white' : 'bg-stone-100 text-stone-600'
                  )}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <h3 className="font-semibold text-text-primary">{m.label}</h3>
                    {m.estimatedMinutes && (
                      <span className="text-xs text-text-muted shrink-0">
                        ~{m.estimatedMinutes} min
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-medium text-sage-700 mb-1">{m.tagline}</p>
                  <p className="text-sm text-text-secondary leading-relaxed">{m.description}</p>
                </div>
                {isSelected && (
                  <Check className="w-5 h-5 text-sage-600 shrink-0 mt-1" />
                )}
              </div>
            </button>
          );
        })}
      </div>

      {mission === 'research' && (
        <Card className="p-5 border-sage-200 bg-sage-50/40">
          <label className="block text-sm font-semibold text-text-primary mb-2">
            Which brands should Dippy research?
          </label>
          <p className="text-xs text-text-secondary mb-3">
            Add 1–10 brand names. Press Enter to add.
          </p>
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              className="flex-1 px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sage-500"
              value={newBrand}
              onChange={(e) => setNewBrand(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addBrand();
                }
              }}
              placeholder="e.g. Litter-Robot"
            />
            <button
              type="button"
              onClick={addBrand}
              className="px-3 py-2 bg-sage-600 text-white rounded-lg text-sm hover:bg-sage-700"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {researchBrands.map((brand) => (
              <span
                key={brand}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-sage-300 text-sage-800 text-sm rounded-full"
              >
                {brand}
                <button
                  type="button"
                  onClick={() => removeBrand(brand)}
                  className="hover:bg-sage-100 rounded-full p-0.5"
                  aria-label={`Remove ${brand}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            {researchBrands.length === 0 && (
              <span className="text-xs text-text-muted italic">Add at least one brand to continue.</span>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
